import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Card, Row, Col, Space, Tag, Typography, Spin, Empty, Button, Tooltip, message, Alert } from 'antd';
import { EyeOutlined, MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { api, fetchLeadGroups } from './api';
import { formatAmount, formatCvrNumber, formatDateTimeShort } from './constants';
import type { LeadGroupSummary, LeadGroupMember } from './types';
import type { LeadTransitionAction } from './leadTransitions';
import { PipelineStatusMenu } from './components/PipelineControls';

const { Title, Text, Paragraph } = Typography;

type LeadGroupsPageProps = {
  onOpenLead?: (leadId: string) => void;
  canManage?: boolean;
  onAgreementRequest?: (leadId: string) => void;
};

export default function LeadGroupsPage({ onOpenLead, canManage = false, onAgreementRequest }: LeadGroupsPageProps) {
  const [groups, setGroups] = useState<LeadGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchLeadGroups();
      setGroups(data);
    } catch (err: any) {
      setError(err?.message || 'Kunne ikke hente samlede leads');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const performStatusUpdate = useCallback(
    async (leadId: string, nextStatus: string, extraPayload?: Record<string, unknown>) => {
      setStatusUpdating(true);
      try {
        const response = await api(`/api/leads/${leadId}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ toStatus: nextStatus, ...extraPayload })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error((payload as any)?.error || 'Kunne ikke opdatere status');
        }
        message.success('Status opdateret');
        await loadGroups();
        return true;
      } catch (err: any) {
        message.error(err?.message || 'Kunne ikke opdatere status');
        return false;
      } finally {
        setStatusUpdating(false);
      }
    },
    [loadGroups]
  );

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  const hasGroups = groups.length > 0;

  const handleStatusAction = useCallback(
    async (member: LeadGroupMember, action: LeadTransitionAction) => {
      const nextStatus = action.to;
      if (!nextStatus || nextStatus === member.status) {
        return;
      }

      if (nextStatus === 'AGREEMENT_MADE') {
        onAgreementRequest?.(member.id);
        return;
      }

      await performStatusUpdate(member.id, nextStatus);
    },
    [onAgreementRequest, performStatusUpdate]
  );

  return (
    <>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Samlede leads
            </Title>
            <Text type="secondary">Overblik over grupperede leads og deres pipeline-status</Text>
          </div>
          <Button onClick={() => loadGroups()} loading={loading} type="default">
            Opdater
          </Button>
        </Space>

        {error ? <Alert type="error" message={error} showIcon /> : null}

        {loading ? (
          <Spin />
        ) : !hasGroups ? (
          <Empty description="Ingen samlede leads endnu" />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {groups.map((group) => (
              <Card
                key={group.id}
                title={group.display_name}
                extra={<Tag color="blue">{group.member_count} lead{group.member_count === 1 ? '' : 's'}</Tag>}
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {group.notes ? <Paragraph>{group.notes}</Paragraph> : null}
                  {group.members.length === 0 ? (
                    <Empty description="Ingen leads i denne samling" />
                  ) : (
                    <Row gutter={[16, 16]}>
                      {group.members.map((member) => (
                        <Col xs={24} md={12} key={member.id}>
                          <MemberCard
                            member={member}
                            canManage={canManage}
                            onOpenLead={onOpenLead}
                            onActionSelected={handleStatusAction}
                            transitionsDisabled={statusUpdating}
                          />
                        </Col>
                      ))}
                    </Row>
                  )}
                  <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                    {Array.isArray(group.contact_badges) && group.contact_badges.length > 0 ? (
                      <Space size={8} wrap>
                        {group.contact_badges.map((badge, index) => (
                          <Tag key={`${group.id}-badge-${index}`} color="geekblue">
                            {badge}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary">Ingen kontaktoplysninger registreret endnu.</Text>
                    )}
                  </div>
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </Space>

    </>
  );
}

type MemberCardProps = {
  member: LeadGroupMember;
  canManage: boolean;
  onOpenLead?: (leadId: string) => void;
  onActionSelected?: (member: LeadGroupMember, action: LeadTransitionAction) => Promise<void> | void;
  transitionsDisabled?: boolean;
};

function MemberCard({ member, canManage, onOpenLead, onActionSelected, transitionsDisabled }: MemberCardProps) {
  const contractValue =
    typeof member.contract_value_annual === 'number'
      ? formatAmount(member.contract_value_annual, member.currency || 'DKK')
      : '—';
  const lastUpdated = member.last_updated ? formatDateTimeShort(member.last_updated) : null;

  const contactChips = useMemo(() => {
    const chips: { icon: ReactNode; value: string }[] = [];
    if (member.contact_name) {
      chips.push({ icon: <UserOutlined />, value: member.contact_name });
    }
    if (member.contact_phone) {
      chips.push({ icon: <PhoneOutlined />, value: member.contact_phone });
    }
    if (member.contact_email) {
      chips.push({ icon: <MailOutlined />, value: member.contact_email });
    }
    return chips;
  }, [member.contact_email, member.contact_name, member.contact_phone]);

  return (
    <Card size="small" bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Space size={8} wrap>
          <Text strong>{member.company_name || 'Ukendt virksomhed'}</Text>
          {member.cvrnummer ? <Tag>{formatCvrNumber(member.cvrnummer)}</Tag> : null}
        </Space>
        <Text type="secondary">Kontraktværdi: {contractValue}</Text>
        {contactChips.length > 0 ? (
          <Space size={4} wrap>
            {contactChips.map((chip, index) => (
              <Tag key={`${member.id}-contact-${index}`} icon={chip.icon}>
                {chip.value}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">Ingen kontaktoplysninger</Text>
        )}
        {lastUpdated ? <Text type="secondary">Opdateret {lastUpdated}</Text> : null}
      </Space>

      <Space align="center" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <PipelineStatusMenu
          status={member.status || 'NOT_CONTACTED'}
          disabled={!canManage || transitionsDisabled}
          onActionSelected={onActionSelected ? (action) => onActionSelected(member, action) : undefined}
        />
        <Tooltip title="Åbn lead-detaljer">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => onOpenLead?.(member.id)}
            disabled={!onOpenLead}
          >
            Åbn lead
          </Button>
        </Tooltip>
      </Space>
    </Card>
  );
}
